import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import Autosuggest from 'react-autosuggest';
import InfiniteScroll from 'react-infinite-scroller';
import { Result, PagedResult } from '../../services/ServiceBase';


type ItemID = any;

export type AutocompleteAsyncProps<T> = {
    loadById: (id: ItemID) => Promise<Result<T>>;
    dataSourceDependentParams?: any[];
    searchByKeywordPaged: (keyword: string, offset: number) => Promise<Result<PagedResult<T>>>;
    renderItem: (item: T) => React.ReactNode;
    renderSelectedItem: (item: T) => React.ReactNode;
    onSelectedItemChanged?: (selectedItem: T) => void;
    getId: (item: T) => ItemID;
    selectedValue?: ItemID | undefined;
    allowNullSelection?: boolean;
    nullSelectionText?: string;
    className?: string;
    getStringRepresentation: (item: T) => string;
    allowNonExistingSuggestion?: boolean;
    createModelBasedOnId?: (id: ItemID) => T;
    onBlur?: (e: React.FocusEvent<any>) => void;
    onEnterKeyPressed?: () => void;
}

function AutocompleteControlAsync<T>(props: AutocompleteAsyncProps<T>) {

    const mountedRef = useRef(true);    

    // autocomplete options

    const scrollParentRef = useRef(null);
    const [canLoadMore, setCanLoadMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState<T>();
    const [autoCompleteValue, setAutocompleteValue] = useState('');
    const [suggestions, setSuggestions] = useState<T[]>([]);
    const [triggerOnEnter, setTriggerOnEnter] = useState(false);

    const [lastKeywordSearch, setLastKeywordSearch] = useState('');
    const searchByKeyword = (keyword) => {

        setLastKeywordSearch(keyword);

        setIsLoading(true);
        props.searchByKeywordPaged(keyword, 0).then(
            result => {
                if (!mountedRef.current)
                    return null;
                if (!result.hasErrors) {

                    if (result.value.Items.length == 0 && props.allowNonExistingSuggestion && props.createModelBasedOnId) {

                        let modelBasedOnId = props.createModelBasedOnId(keyword);
                        if (modelBasedOnId) {
                            result.value.Items = [modelBasedOnId];
                            result.value.TotalItems = 1;
                        }
                    }

                    setSuggestions(result.value.Items);
                    setCanLoadMore(result.value.Items.length < result.value.TotalItems);
                }

                setIsLoading(false);
            }
        );
    };

    const loadMore = () => {

        setIsLoading(true);
        props.searchByKeywordPaged(lastKeywordSearch, suggestions.length).then(result => {

            let newItems = suggestions.concat(result.value.Items);
            setSuggestions(newItems);
            setCanLoadMore(newItems.length < result.value.TotalItems);

            setIsLoading(false);
        });
    }

    const findById = () => {
        setIsLoading(true);
        props.loadById(props.selectedValue).then(result => {

            if (!mountedRef.current) return null;

            if (!result.hasErrors) {

                if (!result.value && props.allowNonExistingSuggestion && props.createModelBasedOnId) {
                    result.value = props.createModelBasedOnId(props.selectedValue);
                }

                if (selectedItem != result.value)
                    setSelectedItem(result.value);
                //debugger;

                let newValue = props.getStringRepresentation(result.value);
                //debugger;
                if (newValue != autoCompleteValue)
                    setAutocompleteValue(newValue);
            }
            setIsLoading(false);
        });
    }

    // end autocomplete

    useEffect(() => {

        if (!mountedRef.current)
            return;

        findById();

    }, [props.selectedValue].concat(props.dataSourceDependentParams || []))

    useEffect(() => {
        if (!mountedRef.current)
            return;

        props.onSelectedItemChanged(selectedItem);

    }, [selectedItem]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    }, [])

    const resolveValues = () => {
        if (props.selectedValue) {

            let correctValue = props.getStringRepresentation(selectedItem);

            if (autoCompleteValue.toLowerCase() != correctValue.toLowerCase()) {

                if (props.allowNonExistingSuggestion && props.createModelBasedOnId) {
                    let modelForNonSuggestion = props.createModelBasedOnId(autoCompleteValue);
                    props.onSelectedItemChanged(modelForNonSuggestion);
                }
                else {
                    props.onSelectedItemChanged(undefined);
                }

            } else if (autoCompleteValue != correctValue) {
                setAutocompleteValue(correctValue);
            }

        } else {
            if ((autoCompleteValue || '').length > 0 && (suggestions || []).length > 0) {
                let candidate = suggestions.find(x => autoCompleteValue.toLowerCase() == props.getStringRepresentation(x).toLowerCase());

                if (!candidate && props.allowNonExistingSuggestion && props.createModelBasedOnId)
                    candidate = props.createModelBasedOnId(autoCompleteValue);

                if (candidate)
                    props.onSelectedItemChanged(candidate);
            }
        }
    }

    return (
        <div className="autosuggest-field-container">
            <div className="autosuggest-field" >
                <Autosuggest ref={mountedRef}
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={({ value }) => {
                        searchByKeyword(value);
                    }}
                    onSuggestionsClearRequested={() => {
                        setSuggestions([])
                    }}
                    shouldRenderSuggestions={() => true}
                    onSuggestionSelected={(event, { suggestion }) => {
                        props.onSelectedItemChanged(suggestion);
                    }}
                    getSuggestionValue={props.getStringRepresentation}
                    renderSuggestion={(suggestion) => props.renderItem(suggestion)}
                    inputProps={{
                        //placeholder: 'Search..',
                        value: autoCompleteValue,
                        onChange: (e, { newValue }) => {                             
                            setAutocompleteValue(newValue);                                                     
                        },
                        onKeyDown: (e) => {

                            if (e.key === 'Enter' && props.onEnterKeyPressed) {
                                resolveValues();
                                props.onEnterKeyPressed();                                      
                            }
                        },
                        onBlur: (event, { highlightedSuggestion }) => {

                            resolveValues();

                            if (props.onBlur)
                                props.onBlur(event);
                        },
                        className: props.className
                    }}
                    renderSuggestionsContainer={({ containerProps, children, query }) => {

                        //console.log('renderSuggestionsContainer', containerProps, children, query);
                        // className="scroll-wrap" ref={scrollParentRef}
                        if (!children)
                            return null;

                        return (<div {...containerProps} >

                            <InfiniteScroll
                                pageStart={0}
                                loadMore={loadMore}
                                hasMore={canLoadMore && !isLoading}
                                useWindow={false}
                            //getScrollParent={() => scrollParentRef.current}
                            >
                                {children}

                            </InfiniteScroll>

                        </div>)
                    }
                    }
                />
            </div>
            {selectedItem && <a href="#" title="Clear Field" onClick={(e) => { e.preventDefault(); setSelectedItem(undefined); }} className="clear-autocomplet-field"><i className="fa fa-times"></i></a>}
        </div>
    );
}

export default AutocompleteControlAsync;